import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Award, BookOpen, CheckCircle2, ChevronRight, Clock, GraduationCap, PlayCircle, Sparkles, TrendingUp } from "lucide-react";
import { COURSES, AI_MATCHING } from "../data/kabelul";

const StatusPill = ({ status }) => {
  const map = {
    "Completed": { c: "ok", icon: CheckCircle2 },
    "In Progress": { c: "warn", icon: PlayCircle },
    "Not Started": { c: "info", icon: BookOpen },
    "Coming Soon": { c: "muted", icon: Clock },
  };
  const it = map[status] || map["Not Started"];
  return <span className={`k-status-pill ${it.c}`}><it.icon size={11} /> {status}</span>;
};

export default function LearningPage({ setPage }) {
  const [filter, setFilter] = useState("All");

  const tabs = ["All", "Recommended", "In Progress", "Completed"];
  const list = COURSES.filter((c) => {
    if (filter === "All") return true;
    if (filter === "Recommended") return c.recommended;
    return c.status === filter;
  });

  const totalUnlocks = AI_MATCHING.potentialIfLearn.reduce((s, x) => s + x.unlocks, 0);

  return (
    <div className="page-inner">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="page-intro">
        <div>
          <p className="eyebrow">LEARNING CENTER · CERTIFY YOUR SKILLS</p>
          <h1>Grow your Passport</h1>
          <p className="subtitle">Complete courses to add verified skills — and unlock more opportunities.</p>
        </div>
        <div className="k-learning-hero">
          <div className="k-learning-icon"><Sparkles size={17} /></div>
          <div>
            <b>Learning → Qualification → Opportunity</b>
            <small>Recommended courses could unlock <strong>{totalUnlocks} more opportunities</strong> for you.</small>
          </div>
        </div>
      </motion.div>

      <div className="k-toolbar">
        <div className="k-tabs" data-testid="learning-tabs">
          {tabs.map((t) => (
            <button
              key={t}
              className={filter === t ? "k-tab active" : "k-tab"}
              onClick={() => setFilter(t)}
              data-testid={`learning-tab-${t.toLowerCase().replaceAll(" ", "-")}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="k-course-grid">
        {list.map((c, i) => (
          <div className={`k-course-card ${c.recommended ? "recommended" : ""}`} key={c.id} data-testid={`course-card-${i}`}>
            {c.recommended && <span className="k-course-flag"><Sparkles size={10} /> Recommended</span>}
            <div className="k-course-head">
              <div className={`k-course-icon ${c.tone}`}><GraduationCap size={18} /></div>
              <span className={`k-course-tag ${c.tone}`}>{c.tag}</span>
            </div>
            <b className="k-course-title">{c.title}</b>
            <small className="k-course-provider">{c.provider}</small>
            <p className="k-course-desc">{c.description}</p>

            <div className="k-course-progress">
              <div className="k-progress-bar">
                <div className="k-progress-fill" style={{ width: `${c.progress}%` }} />
              </div>
              <div className="k-progress-meta">
                <StatusPill status={c.status} />
                <span>{c.progress}%</span>
              </div>
            </div>

            <div className="k-course-meta">
              <span><Clock size={11} /> {c.duration}</span>
              <span>{c.modules} modules</span>
              {c.certified && <span className="ok"><Award size={11} /> Certified</span>}
            </div>

            {c.recommended && c.reason && (
              <div className="k-course-reason">
                <TrendingUp size={12} /> {c.reason}
              </div>
            )}

            {c.status === "Completed" ? (
              <button className="secondary-button k-course-action" onClick={() => setPage("qualifications")} data-testid={`course-view-cert-${i}`}>
                <Award size={13} /> View certificate
              </button>
            ) : c.status === "Coming Soon" ? (
              <button className="secondary-button k-course-action" disabled data-testid={`course-notify-${i}`}>
                <Clock size={13} /> Coming soon
              </button>
            ) : (
              <button
                className="primary-button k-course-action"
                onClick={() => toast.success(c.status === "In Progress" ? `Resuming ${c.title}` : `Enrolled in ${c.title}`)}
                data-testid={`course-enroll-${i}`}
              >
                <PlayCircle size={13} />
                {c.status === "In Progress" ? "Continue" : "Start course"}
              </button>
            )}

            {c.unlocks > 0 && (
              <div className="k-course-unlock">
                <span>Unlocks <strong>{c.unlocks} opportunities</strong></span>
                <button className="quiet-button" onClick={() => setPage("opportunities")}>Preview <ChevronRight size={11} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
